import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { LocalTime } from '~/components/common/LocalTime';
import { ListToolbar, PageHeader, PageShell } from '~/components/layout';
import { aiStatus } from '~/components/pages/ai/common/statusMaps';
import PlacementFilterDrawer from '~/components/pages/ai/placements/PlacementFilterDrawer';
import {
  Card,
  CardBody,
  ConfirmModal,
  DataTable,
  Pagination,
  RowActionsMenu,
  type DataTableColumn,
  CopyButton,
  StatusSelect,
  TagsCell,
} from '~/components/ui';
import { useListUrlState } from '~/hooks/useListUrlState';
import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { matchesPlacementFilters } from '~/lib/ai/listFilters';
import { clampPage, pageCount } from '~/lib/listParams';
import {
  useDeletePlacementMutation,
  useDuplicatePlacementMutation,
  useListPlacementsQuery,
  useUpdatePlacementDraftMutation,
} from '~/modules/query/ai/placements';
import type { PlacementConfigSummary } from '~/modules/query/ai/types';
import { routes } from '~/app/routes';

const FILTER_KEYS = ['status', 'component'] as const;
const PAGE_SIZES = [25, 50, 100] as const;

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
];

const displayName = (placement: PlacementConfigSummary): string =>
  placement.name?.trim() || placement.placementKey;

const editorHref = (placementKey: string): string => routes.aiPlacement(placementKey);

export default function PlacementsPage() {
  const list = useListUrlState({
    filterKeys: FILTER_KEYS,
    defaultPageSize: 50,
    pageSizes: PAGE_SIZES,
  });

  const { data, isLoading, isFetching, error, refetch } = useListPlacementsQuery();
  const [deletePlacement, { isLoading: isDeleting }] = useDeletePlacementMutation();
  const [duplicatePlacement] = useDuplicatePlacementMutation();
  const [updatePlacementDraft] = useUpdatePlacementDraftMutation();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [deleting, setDeleting] = useState<PlacementConfigSummary | null>(null);
  const [duplicatingKey, setDuplicatingKey] = useState<string | null>(null);
  const [statusSavingKey, setStatusSavingKey] = useState<string | null>(null);

  const filtered = Boolean(list.q) || list.filterCount > 0;
  const placements = useMemo(() => data?.placements ?? [], [data]);

  const componentOptions = useMemo(() => {
    const ids = new Set<string>();

    for (const placement of placements) {
      if (placement.componentId.trim()) {
        ids.add(placement.componentId);
      }
    }

    return Array.from(ids).sort((left, right) => left.localeCompare(right));
  }, [placements]);

  const rows = useMemo(
    () =>
      placements.filter((placement) =>
        matchesPlacementFilters(placement, {
          q: list.q,
          status: list.filters.status,
          component: list.filters.component,
        }),
      ),
    [list.filters, list.q, placements],
  );

  const pages = pageCount(rows.length, list.pageSize);
  const page = clampPage(list.page, rows.length, list.pageSize);
  const visibleRows = rows.slice((page - 1) * list.pageSize, page * list.pageSize);

  const duplicate = async (placement: PlacementConfigSummary) => {
    setDuplicatingKey(placement.placementKey);

    try {
      const response = await duplicatePlacement({ placementKey: placement.placementKey }).unwrap();
      toast.success(`Placement duplicated as ${response.placement.placementKey}`);
    } catch (mutationError) {
      toast.error(
        apiErrorMessage(mutationError as ApiErrorInput) ??
          'The placement could not be duplicated. Try again.',
      );
    } finally {
      setDuplicatingKey(null);
    }
  };

  const changeStatus = async (placement: PlacementConfigSummary, status: string) => {
    if (status === placement.status || (status !== 'active' && status !== 'draft')) {
      return;
    }

    setStatusSavingKey(placement.placementKey);

    try {
      await updatePlacementDraft({
        placementKey: placement.placementKey,
        updates: {
          status,
          changeNote: `Status set to ${status === 'active' ? 'Active' : 'Draft'} from placements list`,
        },
      }).unwrap();
      toast.success(`Status updated for ${placement.placementKey}`);
    } catch (mutationError) {
      toast.error(
        apiErrorMessage(mutationError as ApiErrorInput) ??
          'The status could not be updated. Try again.',
      );
    } finally {
      setStatusSavingKey(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting || isDeleting) {
      return;
    }

    const placement = deleting;

    try {
      await deletePlacement(placement.placementKey).unwrap();
      setDeleting(null);
      toast.success(`Placement ${displayName(placement)} deleted`);
    } catch (mutationError) {
      toast.error(
        apiErrorMessage(mutationError as ApiErrorInput) ??
          'The placement could not be deleted. Try again.',
      );
    }
  };

  const columns: DataTableColumn<PlacementConfigSummary>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (placement) => (
        <div className="max-w-md">
          {placement.name?.trim() ? (
            <p className="font-medium text-gray-800 dark:text-white/90">{placement.name.trim()}</p>
          ) : null}
          <div className="flex items-center gap-1">
            <p
              className={
                placement.name?.trim()
                  ? 'font-mono text-xs text-gray-500 dark:text-gray-400'
                  : 'font-medium text-gray-800 dark:text-white/90'
              }
            >
              {placement.placementKey}
            </p>
            <CopyButton
              value={placement.placementKey}
              label={`Copy placement key ${placement.placementKey}`}
              toastMessage={`Placement key copied: ${placement.placementKey}`}
              testId={`ai-placements-copy-${placement.placementKey}`}
            />
          </div>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (placement) => (
        <StatusSelect
          value={placement.status}
          options={STATUS_OPTIONS}
          toneOf={(status) => aiStatus(status).tone}
          disabled={statusSavingKey === placement.placementKey}
          ariaLabel={`Status of ${displayName(placement)}`}
          testId={`ai-placements-status-${placement.placementKey}`}
          onChange={(status) => void changeStatus(placement, status)}
        />
      ),
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: () => <TagsCell tags={[]} />,
    },
    {
      id: 'component',
      header: 'Component',
      cell: (placement) => (
        <span className="font-mono text-xs text-gray-800 dark:text-gray-200">
          {placement.componentId}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      wrap: true,
      cell: (placement) =>
        placement.description?.trim() || (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        ),
    },
    {
      id: 'model',
      header: 'Model',
      cell: (placement) =>
        placement.model || <span className="text-gray-400 dark:text-gray-500">—</span>,
    },
    {
      id: 'updated',
      header: 'Updated',
      cell: (placement) => (
        <div>
          <LocalTime value={placement.updatedAt} />
          {placement.updatedByName && (
            <p className="text-xs text-gray-500 dark:text-gray-400">by {placement.updatedByName}</p>
          )}
        </div>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: (placement) => (
        <div>
          <LocalTime value={placement.createdAt ?? placement.updatedAt} />
          {placement.createdByName && (
            <p className="text-xs text-gray-500 dark:text-gray-400">by {placement.createdByName}</p>
          )}
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (placement) => (
        <RowActionsMenu
          testId={`ai-placements-actions-${placement.placementKey}`}
          ariaLabel={`Open actions for ${placement.placementKey}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: editorHref(placement.placementKey),
            },
            {
              label: 'Duplicate',
              testId: 'duplicate',
              icon: <Copy className="size-4" aria-hidden="true" />,
              disabled: duplicatingKey === placement.placementKey,
              disabledReason: 'This placement is being duplicated.',
              onClick: () => void duplicate(placement),
            },
            {
              label: 'Delete',
              testId: 'delete',
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              destructive: true,
              disabled: duplicatingKey === placement.placementKey,
              disabledReason: 'This placement is being duplicated.',
              onClick: () => setDeleting(placement),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Placements"
        breadcrumbs={[{ label: 'AI' }, { label: 'Placements' }]}
        actions={[
          {
            label: 'Create placement',
            kind: 'primary',
            testId: 'ai-placements-new',
            startIcon: <Plus className="size-4" aria-hidden="true" />,
            href: routes.aiPlacementNew,
          },
        ]}
      />

      <Card>
        <ListToolbar
          title="Placements"
          description="Every place an AI prompt appears in the app, and the model behind it."
          search={{
            value: list.q,
            onChange: list.setQ,
            placeholder: 'Search name, key, component or model…',
            testId: 'ai-placements-search',
          }}
          filters={{
            count: list.filterCount,
            onClick: () => setIsFilterOpen(true),
            onClear: list.clear,
            testId: 'ai-placements-filters',
          }}
        />
        <CardBody className="p-0">
          <DataTable<PlacementConfigSummary>
            framed={false}
            testId="ai-placements-table"
            loading={isLoading && !data}
            refreshing={isFetching && !!data}
            error={
              error && !data
                ? { what: 'placements', message: apiErrorMessage(error), onRetry: () => refetch() }
                : undefined
            }
            emptyMessage={filtered ? 'No placements match.' : 'No placements yet.'}
            emptyDescription={
              filtered
                ? 'Clear the search or filters and try again.'
                : 'Create a placement to give a component an AI prompt and model.'
            }
            data={visibleRows}
            rowKey={(placement) => placement.placementKey}
            href={(placement) => editorHref(placement.placementKey)}
            rowLabel={(placement) => `Edit ${displayName(placement)}`}
            columns={columns}
          />
        </CardBody>
        <Pagination
          pageIndex={page - 1}
          pageSize={list.pageSize}
          pageCount={pages}
          totalRows={rows.length}
          onPageChange={(index) => list.setPage(index + 1)}
          pageSizeControl={{
            value: list.pageSize,
            onChange: list.setPageSize,
            options: [...list.pageSizes],
          }}
        />
      </Card>

      <PlacementFilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        value={list.filters}
        componentOptions={componentOptions}
        onApply={list.setFilters}
        onClear={list.clear}
      />

      <ConfirmModal
        isOpen={deleting !== null}
        title="Delete placement"
        message={`Delete ${deleting ? displayName(deleting) : 'this placement'}?`}
        confirmText={deleting ? displayName(deleting) : undefined}
        confirmLabel="Delete placement"
        variant="danger"
        dangerNote="Its version history and test runs are deleted with it. This cannot be undone."
        testId="ai-placements-delete"
        onClose={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
      />
    </PageShell>
  );
}
